import { Box, Grid, LinearProgress, Typography, useTheme } from '@mui/material';
import { useMutation } from '@parkchirp/api-client';
import React, { FC, useCallback, useMemo, useState } from 'react';

import { BULK_LOCK_OR_UNLOCK_PARKERS, INSERT_PARKERS_IN_SEQUENCE, UPDATE_TIER_NUMBERS } from '../../../mutations';
import { SetShowDialog, SetShowSnackbar } from '../../../types';
import {
	BaseTierRate,
	FutureParker,
	OrderableTier,
	SelectedParker,
	SequencingCountAndRate,
	SequencingTier,
} from '../../../types/leases';
import { BaseTierList } from './baseTier';
import { FutureParkersTable } from './futureParkersTable';
import { SequencingHeader } from './sequencingHeader';
import { SequencingTierList } from './sequencingTier';

type SequencingSetProps = {
	subscriptionId: string;
	allTiers: SequencingTier[];
	setNumber: number;
	maxSetNumber: number;
	maxSequenceNumber: number;
	futureParkers: FutureParker[];
	countsAndRates: SequencingCountAndRate[];
	baseTierRates: BaseTierRate[];
	resequencingInProgress: boolean;
	tierDataLoading: boolean;
	countsAndRatesLoading: boolean;
	baseTierRatesLoading: boolean;
	futureParkersLoading: boolean;
	loadQuietly: boolean;
	handleWaitForResequencing: (x?: boolean) => Promise<void>;
	pollSequencingTiers: () => Promise<void>;
	setShowSnackbar: SetShowSnackbar;
	setShowDialog: SetShowDialog;
};

export const SequencingSet: FC<SequencingSetProps> = ({
	subscriptionId,
	allTiers,
	setNumber,
	maxSetNumber,
	maxSequenceNumber,
	futureParkers,
	countsAndRates,
	baseTierRates,
	resequencingInProgress,
	tierDataLoading,
	countsAndRatesLoading,
	baseTierRatesLoading,
	futureParkersLoading,
	loadQuietly,
	handleWaitForResequencing,
	pollSequencingTiers,
	setShowSnackbar,
	setShowDialog,
}) => {
	const tiers = useMemo(() => allTiers.filter((tier) => tier.tierNumber > 0), [allTiers]);
	const baseTiers = useMemo(() => allTiers.filter((tier) => tier.tierNumber === 0), [allTiers]);

	const [selectedParkers, setSelectedParkers] = useState<SelectedParker[]>([]);
	const [inBulkSequencingMode, setInBulkSequencingMode] = useState<boolean>(false);

	const theme = useTheme();

	const [updateTierNumbers, { loading: updateInProgress }] = useMutation(UPDATE_TIER_NUMBERS);
	const [insertParkersInSequence, { loading: bulkInsertInProgress }] = useMutation(INSERT_PARKERS_IN_SEQUENCE);
	const [bulkLockOrUnlockParkers, { loading: bulkLockingInProgress }] = useMutation(BULK_LOCK_OR_UNLOCK_PARKERS);

	const validateBulkResequencing = useCallback(
		(startingSequenceNumber: number) => {
			if (selectedParkers.length === 0) {
				return 'No parkers selected';
			}

			if (!Number.isInteger(startingSequenceNumber)) {
				return 'Sequence Number must be a whole number';
			}

			const maxStartingSequenceNumber = maxSequenceNumber - selectedParkers.length + 1;

			if (startingSequenceNumber > maxStartingSequenceNumber) {
				return `Invalid Sequence Number - Sequence number must be no greater than ${maxStartingSequenceNumber}`;
			}

			return null;
		},
		[maxSequenceNumber, selectedParkers],
	);

	const addSelectedParker = useCallback(
		({ id, sequenceNumber }: SelectedParker) => {
			const updatedParkers = [...selectedParkers, { id, sequenceNumber }];
			setSelectedParkers(updatedParkers);
		},
		[selectedParkers, setSelectedParkers],
	);

	const removeSelectedParker = useCallback(
		(parkerId: string) => {
			const updatedParkers = selectedParkers.filter((parker) => parker.id !== parkerId);
			setSelectedParkers(updatedParkers);
		},
		[selectedParkers, setSelectedParkers],
	);

	const handleBulkResequenceCancel = useCallback(() => {
		setSelectedParkers([]);
		setInBulkSequencingMode(false);
	}, [setSelectedParkers, setInBulkSequencingMode]);

	const handleBulkResequencing = useCallback(
		async (startingSequenceNumber: number) => {
			const validationError = validateBulkResequencing(startingSequenceNumber);

			if (validationError) {
				setShowSnackbar({
					message: validationError,
					severity: 'error',
					autoHideDuration: 5000,
				});

				return;
			}

			try {
				await insertParkersInSequence({
					variables: {
						subscriptionId,
						parkerIds: selectedParkers.map((p) => p.id),
						sequenceNumber: startingSequenceNumber,
					},
					context: { apiVersion: 'v2' },
					onCompleted: async () => {
						setShowSnackbar({
							message: 'Parker sequence numbers updated',
							severity: 'success',
							autoHideDuration: 3000,
						});

						await handleWaitForResequencing();

						setSelectedParkers([]);
						setInBulkSequencingMode(false);
					},
				});
			} catch {
				setShowSnackbar({
					message: 'Failed to update parker sequence numbers',
					severity: 'error',
					autoHideDuration: 5000,
				});
			}
		},
		[
			selectedParkers,
			subscriptionId,
			setShowSnackbar,
			validateBulkResequencing,
			handleWaitForResequencing,
			insertParkersInSequence,
		],
	);

	const reorderTiers = useCallback(
		async (updatedTiers: OrderableTier[]) => {
			const reorderedTiers = updatedTiers.map((tier) => ({
				id: tier.id,
				tierNumber: tier.updatedTierNumber,
			}));

			try {
				setShowDialog(false);

				await updateTierNumbers({
					variables: {
						subscriptionId,
						tiers: reorderedTiers,
					},
					context: { apiVersion: 'v2' },
					onCompleted: async () => {
						setShowSnackbar({
							message: 'Tiers successfully reordered',
							severity: 'success',
							autoHideDuration: 3000,
						});

						await handleWaitForResequencing();
					},
				});
			} catch {
				setShowSnackbar({
					message: 'Failed to reorder tiers',
					severity: 'error',
					autoHideDuration: 5000,
				});
			}
		},
		[subscriptionId, updateTierNumbers, handleWaitForResequencing, setShowDialog, setShowSnackbar],
	);

	const handleBulkLockOrUnlock = useCallback(
		async (isLocked: boolean) => {
			try {
				setShowDialog(null);

				await bulkLockOrUnlockParkers({
					variables: {
						subscriptionId,
						setNumber,
						isLocked,
					},
					context: { apiVersion: 'v2' },
					onCompleted: async () => {
						setShowSnackbar({
							message: 'Parkers successfully updated',
							severity: 'success',
							autoHideDuration: 3000,
						});

						await handleWaitForResequencing(false);
					},
				});
			} catch {
				setShowSnackbar({
					message: 'Failed to update parkers',
					severity: 'error',
					autoHideDuration: 5000,
				});
			}
		},
		[setNumber, subscriptionId, bulkLockOrUnlockParkers, handleWaitForResequencing, setShowDialog, setShowSnackbar],
	);

	const loading =
		countsAndRatesLoading ||
		tierDataLoading ||
		baseTierRatesLoading ||
		updateInProgress ||
		bulkInsertInProgress ||
		futureParkersLoading ||
		bulkLockingInProgress;

	if (loading && !loadQuietly) {
		return <LinearProgress sx={{ width: '100%' }} />;
	}

	if (resequencingInProgress) {
		return <LinearProgress sx={{ width: '100%' }} />;
	}

	return (
		<Grid container spacing={3} mt={3}>
			{loadQuietly && <LinearProgress sx={{ width: '100%' }} />}
			<Grid item sm={6}>
				<Box>
					<Typography variant='h5' sx={{ marginBottom: '10px', fontWeight: 400 }}>
						Sequencing Tiers
					</Typography>
					<SequencingHeader
						baseTierRates={baseTierRates}
						countsAndRates={countsAndRates}
						inBulkSequencingMode={inBulkSequencingMode}
						loading={loading}
						subscriptionId={subscriptionId}
						theme={theme}
						tiers={tiers}
						setNumber={setNumber}
						maxSetNumber={maxSetNumber}
						handleClose={() => setShowDialog(false)}
						reorderTiers={reorderTiers}
						pollSequencingTiers={pollSequencingTiers}
						setInBulkSequencingMode={setInBulkSequencingMode}
						setShowDialog={setShowDialog}
						setShowSnackbar={setShowSnackbar}
						handleBulkResequencing={handleBulkResequencing}
						handleBulkResequenceCancel={handleBulkResequenceCancel}
						handleBulkLockOrUnlock={handleBulkLockOrUnlock}
					/>
					{tiers?.map((tier) => (
						<SequencingTierList
							key={tier.id}
							tier={tier}
							setShowDialog={setShowDialog}
							setShowSnackbar={setShowSnackbar}
							inBulkSequencingMode={inBulkSequencingMode}
							maxSequenceNumber={maxSequenceNumber}
							addSelectedParker={addSelectedParker}
							removeSelectedParker={removeSelectedParker}
							handleWaitForResequencing={handleWaitForResequencing}
						/>
					))}
					{baseTiers.length > 0 && (
						<BaseTierList
							tiers={baseTiers}
							setShowDialog={setShowDialog}
							setShowSnackbar={setShowSnackbar}
							theme={theme}
							inBulkSequencingMode={inBulkSequencingMode}
							maxSequenceNumber={maxSequenceNumber}
							addSelectedParker={addSelectedParker}
							removeSelectedParker={removeSelectedParker}
							handleWaitForResequencing={handleWaitForResequencing}
						/>
					)}
				</Box>
			</Grid>
			<Grid item sm={6}>
				<Typography variant='h5'>Future Parkers</Typography>
				<FutureParkersTable
					parkers={futureParkers}
					loading={futureParkersLoading}
					setShowSnackbar={setShowSnackbar}
					handleWaitForResequencing={handleWaitForResequencing}
				/>
			</Grid>
		</Grid>
	);
};
